import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Loader2,
  PlusCircle,
  Search,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSse } from '@/hooks/useSse';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn, formatINR } from '@/lib/utils';

interface TxnLogRow {
  id: string; // otp_log_id (uuid)
  occurredAt: string;
  direction: 'INFLOW' | 'OUTFLOW';
  type: 'DEBIT' | 'CREDIT' | 'REFUND';
  amount: string | null;
  currency: string | null;
  cardId: string | null;
  cardLastFour: string | null;
  cardBank: string | null;
  cardNickname: string | null;
  bankAccountId: string | null;
  bankNickname: string | null;
  bankName: string | null;
  vendorOrPayer: string | null;
  bankRef: string | null;
  smsBody: string | null;
  senderId: string;
  otpMethod: 'WITH_OTP' | 'WITHOUT_OTP' | null;
  attributedClientId: string | null;
  attributedClientName: string | null;
  attributedVendor: string | null;
  attributedBookingHead: string | null;
  attributedType: string | null;
  attributedNotes: string | null;
  isTagged: boolean;
  ageHours: number;
  raisedByUserId: string | null;
  raisedByName: string | null;
  raisedByVia: 'CARD_REQUEST' | 'BANK_TRANSFER_REQUEST' | 'WALLET_SESSION' | null;
}

interface TxnLogResponse {
  rows: TxnLogRow[];
  total: number;
  nextCursor: number | null;
}

interface Counts {
  untagged: number;
  tagged: number;
  total: number;
}

interface ClientItem {
  id: string;
  name: string;
}

interface InstrumentResponse {
  cards: Array<{ id: string; nickname: string; bank: string; lastFour: string }>;
  bankAccounts: Array<{ id: string; nickname: string; bank: string; accountNumberMasked?: string | null }>;
}

const CLIENT_HEADS = ['FLIGHT', 'HOTEL', 'VISA', 'LAND_PACKAGE', 'INSURANCE', 'ANCILLARY'] as const;
const EXPENSE_HEADS = [
  'SALARY', 'OFFICE_RENT', 'UTILITIES', 'SOFTWARE', 'ASSET', 'SUPPLIES',
  'FOOD', 'TAX', 'COMMISSION', 'BANK_CHARGE', 'REFUND_OUT', 'INTERNAL_TRANSFER', 'ADD_BENEFICIARY', 'OTHER',
] as const;
type Head = (typeof CLIENT_HEADS)[number] | (typeof EXPENSE_HEADS)[number];

interface Props {
  onSseStatus: (s: ReturnType<typeof useSse>['status']) => void;
}

export function TxnLogPage({ onSseStatus }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSee = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';

  const [statusFilter, setStatusFilter] = useState<'UNTAGGED' | 'TAGGED' | 'ALL'>('UNTAGGED');
  const [directionFilter, setDirectionFilter] = useState<'INFLOW' | 'OUTFLOW' | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [scope, setScope] = useState<string>(''); // "" | "card:<id>" | "bank:<id>"
  const [cursor, setCursor] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Live SSE: invalidate the txn log on routing-engine events so new debits
  // and credits surface without a manual refresh.
  const sse = useSse({
    onEvent: (ev) => {
      if (
        ev.type === 'transaction_confirmed' ||
        ev.type === 'capture_pending_attribution' ||
        ev.type === 'otp_ready'
      ) {
        qc.invalidateQueries({ queryKey: ['txn-log'] });
      }
    },
  });
  useEffect(() => {
    onSseStatus(sse.status);
  }, [sse.status, onSseStatus]);

  const queryKey = useMemo(
    () => ['txn-log', { statusFilter, directionFilter, debouncedSearch, scope, cursor }],
    [statusFilter, directionFilter, debouncedSearch, scope, cursor],
  );

  const list = useQuery<TxnLogResponse>({
    queryKey,
    queryFn: () => {
      const p = new URLSearchParams({
        status: statusFilter,
        direction: directionFilter,
        limit: '100',
        cursor: String(cursor),
      });
      if (debouncedSearch) p.set('q', debouncedSearch);
      if (scope.startsWith('card:')) p.set('cardId', scope.slice(5));
      if (scope.startsWith('bank:')) p.set('bankAccountId', scope.slice(5));
      return api(`/api/txn-log?${p}`);
    },
    enabled: canSee,
  });

  const counts = useQuery<Counts>({
    queryKey: ['txn-log-counts', { directionFilter, debouncedSearch, scope }],
    queryFn: () => {
      const p = new URLSearchParams({ direction: directionFilter });
      if (debouncedSearch) p.set('q', debouncedSearch);
      if (scope.startsWith('card:')) p.set('cardId', scope.slice(5));
      if (scope.startsWith('bank:')) p.set('bankAccountId', scope.slice(5));
      return api(`/api/txn-log/counts?${p}`);
    },
    enabled: canSee,
  });

  const instruments = useQuery<InstrumentResponse>({
    queryKey: ['export-instruments'],
    queryFn: () => api('/api/exports/instruments'),
    enabled: canSee,
  });

  if (!canSee) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Restricted</CardTitle>
          <CardDescription>The transaction log is visible to ADMIN and ACCOUNTANT roles only.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FileText className="h-5 w-5" />}
        title="Transaction log"
        subtitle="Every credit and debit, in one place. Tag each one to a client / vendor / booking head — the bell counts what's still untagged."
        meta={
          <div className="flex items-center gap-2">
            {counts.data && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {counts.data.total} total
              </span>
            )}
            <Button size="sm" onClick={() => setManualOpen(true)}>
              <PlusCircle className="mr-1.5 h-3.5 w-3.5" /> Log manual txn
            </Button>
          </div>
        }
      />

      {manualOpen && (
        <ManualLogModal
          instruments={instruments.data}
          onClose={() => setManualOpen(false)}
          onLogged={() => {
            setManualOpen(false);
            qc.invalidateQueries({ queryKey: ['txn-log'] });
            qc.invalidateQueries({ queryKey: ['txn-log-counts'] });
          }}
        />
      )}

      {/* Counts as filter cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <CountCard
          label="Untagged"
          n={counts.data?.untagged ?? 0}
          accent="border-l-rose-500"
          tone="text-rose-700"
          icon={<AlertTriangle className="h-4 w-4 text-rose-600" />}
          active={statusFilter === 'UNTAGGED'}
          onClick={() => { setStatusFilter('UNTAGGED'); setCursor(0); }}
          subtitle="Need a client / vendor / booking head"
        />
        <CountCard
          label="Tagged"
          n={counts.data?.tagged ?? 0}
          accent="border-l-emerald-500"
          tone="text-emerald-700"
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          active={statusFilter === 'TAGGED'}
          onClick={() => { setStatusFilter('TAGGED'); setCursor(0); }}
          subtitle="Closed for the day"
        />
        <CountCard
          label="All txns"
          n={counts.data?.total ?? 0}
          accent="border-l-slate-400"
          tone="text-slate-700"
          icon={<Clock className="h-4 w-4 text-slate-600" />}
          active={statusFilter === 'ALL'}
          onClick={() => { setStatusFilter('ALL'); setCursor(0); }}
          subtitle="Both directions, full window"
        />
      </div>

      {/* Filters row */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search vendor, payer, client, body…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCursor(0); }}
                className="pl-8"
              />
            </div>
            <FilterPill
              label="All flow"
              active={directionFilter === 'ALL'}
              onClick={() => { setDirectionFilter('ALL'); setCursor(0); }}
            />
            <FilterPill
              label="Outflow"
              icon={<ArrowUpFromLine className="h-3 w-3" />}
              active={directionFilter === 'OUTFLOW'}
              onClick={() => { setDirectionFilter('OUTFLOW'); setCursor(0); }}
            />
            <FilterPill
              label="Inflow"
              icon={<ArrowDownToLine className="h-3 w-3" />}
              active={directionFilter === 'INFLOW'}
              onClick={() => { setDirectionFilter('INFLOW'); setCursor(0); }}
            />
            <select
              value={scope}
              onChange={(e) => { setScope(e.target.value); setCursor(0); }}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            >
              <option value="">All instruments</option>
              {instruments.data?.cards.length ? (
                <optgroup label="Cards">
                  {instruments.data.cards.map((c) => (
                    <option key={c.id} value={`card:${c.id}`}>
                      {c.bank} ••{c.lastFour}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {instruments.data?.bankAccounts.length ? (
                <optgroup label="Bank accounts">
                  {instruments.data.bankAccounts.map((b) => (
                    <option key={b.id} value={`bank:${b.id}`}>
                      {b.bank} {b.nickname}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {list.isLoading ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Loading transactions…
          </CardContent>
        </Card>
      ) : (list.data?.rows.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-3 text-sm font-medium">Nothing here</p>
            <p className="text-xs text-muted-foreground">
              {statusFilter === 'UNTAGGED' ? 'All transactions are tagged 🎉' : 'No matches for this filter.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {list.data!.rows.map((r) => (
            <TxnRow key={r.id} row={r} clients={[]} />
          ))}
          {list.data?.nextCursor !== null && list.data?.nextCursor !== undefined ? (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCursor(list.data!.nextCursor!)}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CountCard({
  label,
  n,
  accent,
  tone,
  icon,
  active,
  onClick,
  subtitle,
}: {
  label: string;
  n: number;
  accent: string;
  tone: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'card-premium group relative flex flex-col rounded-xl border border-l-[6px] p-5 text-left',
        accent,
        active ? 'ring-2 ring-primary/30 ring-offset-1' : 'ring-0',
      )}
    >
      <div className="flex items-center gap-1.5 kpi-label">
        {icon}
        {label}
      </div>
      <div className={cn('mt-1 kpi-number', tone)}>{n.toLocaleString('en-IN')}</div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
    </button>
  );
}

function FilterPill({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm transition',
        active ? 'border-primary bg-primary/10 text-primary' : 'border-input hover:bg-muted',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function TxnRow({ row, clients: _clients }: { row: TxnLogRow; clients: ClientItem[] }) {
  const [open, setOpen] = useState(false);
  const inflow = row.direction === 'INFLOW';
  const aging = row.ageHours >= 48 ? 'critical' : row.ageHours >= 24 ? 'warn' : 'fresh';
  const tagged = row.isTagged;

  return (
    <div
      className={cn(
        'rounded-lg border bg-card transition',
        tagged ? 'border-emerald-200' : aging === 'critical' ? 'border-rose-300 bg-rose-50/30' : aging === 'warn' ? 'border-amber-300 bg-amber-50/30' : 'border-rose-200 bg-rose-50/20',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        {/* Status dot */}
        <div className="mt-1 flex flex-col items-center gap-1">
          <span
            className={cn(
              'h-2.5 w-2.5 rounded-full',
              tagged ? 'bg-emerald-500' : aging === 'critical' ? 'bg-rose-600' : aging === 'warn' ? 'bg-amber-500' : 'bg-rose-400',
            )}
          />
        </div>

        {/* Direction icon */}
        <div className="mt-0.5">
          {inflow ? (
            <ArrowDownToLine className="h-4 w-4 text-emerald-600" />
          ) : (
            <ArrowUpFromLine className="h-4 w-4 text-rose-600" />
          )}
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium tabular-nums">
              {row.currency === 'INR' && row.amount
                ? formatINR(parseFloat(row.amount))
                : `${row.currency ?? ''} ${row.amount ?? '—'}`.trim()}
            </span>
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{row.type}</span>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">{instrumentLabel(row)}</span>
            {row.bankRef ? (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                {row.bankRef.slice(0, 14)}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 truncate text-sm text-muted-foreground">
            <span className="font-medium text-foreground/80">{row.vendorOrPayer ?? '—'}</span>
            {row.attributedClientName ? <span className="ml-2">→ {row.attributedClientName}</span> : null}
            {row.attributedBookingHead ? <span className="ml-2">· {prettyHead(row.attributedBookingHead)}</span> : null}
            {row.otpMethod === 'WITHOUT_OTP' ? (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-semibold uppercase text-amber-800">
                no-OTP
              </span>
            ) : null}
          </div>
          {!tagged && row.raisedByName ? (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800">
              chase {row.raisedByName}
              <span className="text-blue-500">· {raisedByLabel(row.raisedByVia)}</span>
            </div>
          ) : null}
        </div>

        {/* Right: age + chevron */}
        <div className="flex flex-col items-end gap-1">
          <AgePill ageHours={row.ageHours} tagged={tagged} />
          {open ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {open ? <AttributeForm row={row} /> : null}
    </div>
  );
}

function AgePill({ ageHours, tagged }: { ageHours: number; tagged: boolean }) {
  if (tagged) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
        <CheckCircle2 className="h-3 w-3" />
        Tagged
      </span>
    );
  }
  const tone =
    ageHours >= 48
      ? 'bg-rose-100 text-rose-700'
      : ageHours >= 24
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-700';
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tabular-nums tracking-wide', tone)}>
      <Clock className="h-3 w-3" />
      {ageHours}h
    </span>
  );
}

function AttributeForm({ row }: { row: TxnLogRow }) {
  const qc = useQueryClient();

  const [vendor, setVendor] = useState(row.attributedVendor ?? row.vendorOrPayer ?? '');
  const [clientId, setClientId] = useState(row.attributedClientId ?? '');
  const [head, setHead] = useState<Head | ''>((row.attributedBookingHead as Head | null) ?? '');
  const [notes, setNotes] = useState(row.attributedNotes ?? '');

  const clientsQ = useQuery<ClientItem[]>({
    queryKey: ['clients-typeahead'],
    queryFn: () => api('/api/clients?limit=500'),
  });

  const isClientHead = (CLIENT_HEADS as readonly string[]).includes(head);
  const attributionType = isClientHead ? 'CLIENT_BOOKING' : head ? 'OPERATING_EXPENSE' : null;

  const save = useMutation({
    mutationFn: async () => {
      return api(`/api/txn-log/${row.id}/attribute`, {
        method: 'POST',
        body: {
          clientId: clientId || null,
          vendor: vendor.trim() || null,
          bookingHead: head || null,
          attributionType,
          notes: notes.trim() || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['txn-log'] });
      qc.invalidateQueries({ queryKey: ['txn-log-counts'] });
    },
  });

  return (
    <div className="border-t bg-muted/20 px-4 py-3 text-sm">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="text-xs">Vendor / payer</Label>
          <Input
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder={row.direction === 'INFLOW' ? 'who paid us' : 'who we paid'}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Client</Label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm"
          >
            <option value="">— pick a client —</option>
            {(clientsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Booking head</Label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {[...CLIENT_HEADS].map((h) => (
              <HeadChip key={h} label={prettyHead(h)} active={head === h} onClick={() => setHead(head === h ? '' : h as Head)} kind="client" />
            ))}
            <span className="mx-1 self-center text-muted-foreground">·</span>
            {[...EXPENSE_HEADS].map((h) => (
              <HeadChip key={h} label={prettyHead(h)} active={head === h} onClick={() => setHead(head === h ? '' : h as Head)} kind="expense" />
            ))}
          </div>
          {head && !isClientHead ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Operating expense / internal transfer — vendor is enough, no client needed.
            </p>
          ) : null}
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="optional" />
        </div>
      </div>

      {row.smsBody ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:underline">
            Show original SMS
          </summary>
          <pre className="mt-1 whitespace-pre-wrap rounded bg-background/60 p-2 font-mono text-[11px] text-muted-foreground">
            {row.smsBody}
          </pre>
        </details>
      ) : null}

      <div className="mt-3 flex items-center justify-end gap-2">
        {save.isError ? (
          <span className="text-xs text-rose-600">
            {save.error instanceof ApiError ? save.error.message : 'Save failed'}
          </span>
        ) : null}
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
          Save attribution
        </Button>
      </div>
    </div>
  );
}

function HeadChip({
  label,
  active,
  onClick,
  kind,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  kind: 'client' | 'expense';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs transition',
        active
          ? kind === 'client'
            ? 'border-blue-500 bg-blue-50 text-blue-800'
            : 'border-violet-500 bg-violet-50 text-violet-800'
          : 'border-input bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {label}
    </button>
  );
}

function instrumentLabel(row: TxnLogRow): string {
  if (row.cardId && row.cardLastFour) {
    const nick = row.cardNickname ?? '';
    const bank = row.cardBank ?? '';
    const compact = nick.toLowerCase().startsWith(bank.toLowerCase()) ? nick : `${bank} ${nick}`.trim();
    return `${compact} ••${row.cardLastFour}`;
  }
  if (row.bankNickname || row.bankName) {
    return [row.bankName, row.bankNickname].filter(Boolean).join(' ');
  }
  return '—';
}

function prettyHead(s: string): string {
  return s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function raisedByLabel(via: TxnLogRow['raisedByVia']): string {
  switch (via) {
    case 'CARD_REQUEST':
      return 'card OTP raiser';
    case 'BANK_TRANSFER_REQUEST':
      return 'bank transfer raiser';
    case 'WALLET_SESSION':
      return 'wallet booker';
    default:
      return '';
  }
}

/**
 * Manual no-OTP log entry. Off-system debits (Expedia direct charges,
 * netbanking transfers done outside our flow, hotel-hold captures the
 * SMS for hasn't landed yet) — accountant types them in so the books
 * stay complete even when the routing engine never saw the SMS.
 */
function ManualLogModal({
  instruments,
  onClose,
  onLogged,
}: {
  instruments: InstrumentResponse | undefined;
  onClose: () => void;
  onLogged: () => void;
}) {
  const [paymentMethod, setPaymentMethod] = useState<'CARD' | 'BANK_TRANSFER'>('CARD');
  const [instrumentId, setInstrumentId] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [clientName, setClientName] = useState('');
  const [head, setHead] = useState<Head | ''>('');
  const [paymentNature, setPaymentNature] = useState<'CLIENT_BOOKING' | 'OPERATING_EXPENSE'>(
    'CLIENT_BOOKING',
  );
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [notes, setNotes] = useState('');

  const log = useMutation({
    mutationFn: () => {
      const isClient = paymentNature === 'CLIENT_BOOKING';
      return api('/api/transactions/manual', {
        method: 'POST',
        body: {
          paymentMethod,
          cardId: paymentMethod === 'CARD' ? instrumentId : undefined,
          bankAccountId: paymentMethod === 'BANK_TRANSFER' ? instrumentId : undefined,
          amount,
          currency: 'INR',
          vendor: vendor || undefined,
          beneficiaryName: paymentMethod === 'BANK_TRANSFER' ? vendor || undefined : undefined,
          clientName: clientName || undefined,
          paymentNature,
          bookingHead: head || undefined,
          attributionType: isClient ? 'CLIENT' : 'OFFICE_EXPENSE',
          notes: notes || undefined,
          occurredAt: new Date(occurredAt).toISOString(),
        },
      });
    },
    onSuccess: onLogged,
  });

  const heads = paymentNature === 'CLIENT_BOOKING' ? CLIENT_HEADS : EXPENSE_HEADS;
  const cardOpts = instruments?.cards ?? [];
  const bankOpts = instruments?.bankAccounts ?? [];
  const canSubmit =
    !!amount &&
    !!instrumentId &&
    (paymentNature === 'CLIENT_BOOKING' ? !!clientName : true) &&
    !!head;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Transactions</div>
            <div className="text-base font-semibold">Log a manual debit</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-2">
            <SegBtn
              active={paymentNature === 'CLIENT_BOOKING'}
              onClick={() => {
                setPaymentNature('CLIENT_BOOKING');
                setHead('');
              }}
            >
              Client booking
            </SegBtn>
            <SegBtn
              active={paymentNature === 'OPERATING_EXPENSE'}
              onClick={() => {
                setPaymentNature('OPERATING_EXPENSE');
                setHead('');
                setClientName('');
              }}
            >
              Office expense
            </SegBtn>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <SegBtn
              active={paymentMethod === 'CARD'}
              onClick={() => {
                setPaymentMethod('CARD');
                setInstrumentId('');
              }}
            >
              Card
            </SegBtn>
            <SegBtn
              active={paymentMethod === 'BANK_TRANSFER'}
              onClick={() => {
                setPaymentMethod('BANK_TRANSFER');
                setInstrumentId('');
              }}
            >
              Bank transfer
            </SegBtn>
          </div>

          <div>
            <Label className="text-xs">From</Label>
            <select
              value={instrumentId}
              onChange={(e) => setInstrumentId(e.target.value)}
              className="mt-1 w-full rounded border border-input bg-background px-2 py-2 text-sm"
            >
              <option value="">Select instrument</option>
              {paymentMethod === 'CARD'
                ? cardOpts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.bank} {c.nickname} ••{c.lastFour}
                    </option>
                  ))
                : bankOpts.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nickname} · {b.bank}
                    </option>
                  ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount (INR)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="6186.00"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">When</Label>
              <Input
                type="datetime-local"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">
              {paymentMethod === 'CARD' ? 'Vendor' : 'Beneficiary name'}
            </Label>
            <Input
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder={paymentMethod === 'CARD' ? 'Expedia / Booking.com / …' : 'SHIBENDU MUKHOPADHYAY'}
              className="mt-1"
            />
          </div>

          {paymentNature === 'CLIENT_BOOKING' && (
            <div>
              <Label className="text-xs">Client</Label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Client name on the booking"
                className="mt-1"
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Booking head</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {heads.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHead(head === h ? '' : (h as Head))}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition',
                    head === h
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background text-muted-foreground hover:bg-muted',
                  )}
                >
                  {prettyHead(h)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Trip ref, why no SMS, etc."
              className="mt-1"
            />
          </div>

          {log.isError && (
            <div className="rounded bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
              {(log.error as ApiError | Error | null) instanceof ApiError
                ? `Could not save: ${JSON.stringify((log.error as ApiError).body)}`
                : `Could not save: ${(log.error as Error).message}`}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border p-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSubmit || log.isPending} onClick={() => log.mutate()}>
            {log.isPending ? 'Logging…' : 'Log debit'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded border px-3 py-2 text-sm transition',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}
